import * as React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
} from "@mui/material";
import { useI18n } from "../i18n/I18nProvider";
import { supabase } from "../logic/client";


export default function UserProfileSetup(props: {handlePageChange?: (p: string) => void}) {
  const { lang } = useI18n();
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [form, setForm] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    age: "",
    job: "",
    location: "",
    gender: "" as "male" | "female" | "other" | "",
  });

  const handleChange =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm({ ...form, [field]: e.target.value });
    };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
        if (
            !form.firstName ||
            !form.lastName ||
            !form.age ||
            !form.gender ||
            !form.job ||
            !form.location
        ) {
            setError(L("Please fill all fields before continuing.", "Bitte fülle alle Felder aus, bevor du fortfährst."));
            setLoading(false);
            return;
        }
        const { error } = await supabase.rpc("update_my_profile", {
            p_first_name: form.firstName,
            p_last_name: form.lastName,
            p_age: form.age ? Number(form.age) : null,
            p_job: form.job,
            p_location: form.location,
            p_gender: form.gender || null,
        });

        if (error) throw error;

        props.handlePageChange?.("JourneyPillars");

    } catch (err: any) {
        console.error("Profile save error:", err);
        setError(err.message || "Something went wrong");
    } finally {
        setLoading(false);
    }
  };

  React.useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) throw userError;
        if (!user) return;

        const { data, error } = await supabase
          .from("employees")
          .select(
            "first_name, last_name, company_email, age, position, location, gender"
          )
          .eq("auth_user_id", user.id)
          .single();

        if (error) throw error;

        if (data) {
          setForm({
            firstName: data.first_name ?? "",
            lastName: data.last_name ?? "",
            email: data.company_email ?? "",
            age: data.age ? String(data.age) : "",
            job: data.position ?? "",
            location: data.location ?? "",
            gender: data.gender ?? "",
          });
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
  <Box
    sx={{
      width: "100%",
      minHeight: "100vh",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
      py: { xs: 4, md: 6 },
      overflow: "hidden",
    }}
  >
    {/* BACKGROUND */}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: "url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        zIndex: 0,
      }}
    />

    {/* DARK OVERLAY */}
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        background:
          "radial-gradient(circle at center, rgba(0,0,0,0.5), rgba(0,0,0,0.85))",
        zIndex: 1,
      }}
    />

    {/* CONTENT (ONLY ONE CARD) */}
    <Box
      sx={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        maxWidth: {
          xs: "100%",
          sm: 520,
          md: 680,
          lg: 900,
        },
        borderRadius: 3,
        p: { xs: 3, sm: 4, md: 5 },
        backdropFilter: "blur(18px)",
        background: "rgba(0,0,0,0.75)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 0 40px rgba(255,0,0,0.15)",
      }}
    >
      {/* TITLE */}
      <Typography
        variant="h4"
        sx={{
          color: "white",
          mb: 4,
          fontWeight: 500,
        }}
      >
        {L("Tell us about yourself", "Erzähl uns von dir")}
      </Typography>

      {/* FORM */}

      <Typography
        sx={{
          color: "white",
          mb: 4,
          fontWeight: 100,
        }}
      >
        {L("Email: ", "E-mail: ")} {form.email}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr",        // tablet portrait → single column
            md: "1fr 1fr",    // tablet landscape → 2 cols
          },
          gap: { xs: 2, sm: 2.5, md: 2.8 },
          rowGap: { xs: 2.2, sm: 2.8, md: 3 },
        }}
      >

        <TextField
          placeholder={L("First Name", "Vorname")}
          value={form.firstName}
          onChange={handleChange("firstName")}
          fullWidth
          InputProps={inputStyle}
        />

        <TextField
          placeholder={L("Last Name", "Nachname")}
          value={form.lastName}
          onChange={handleChange("lastName")}
          fullWidth
          InputProps={inputStyle}
        />

        <TextField
          placeholder={L("Age", "Alter")}
          type="number"
          value={form.age}
          onChange={handleChange("age")}
          fullWidth
          InputProps={inputStyle}
        />

        <TextField
          select
          value={form.gender}
          onChange={(e) =>
            setForm({ ...form, gender: e.target.value as any })
          }
          fullWidth
          InputProps={inputStyle}
          SelectProps={{
            displayEmpty: true,
          }}
        >
          <MenuItem value="">
            <em>{L("Gender", "Geschlecht")}</em>
          </MenuItem>
          <MenuItem value="male">
            {L("Male", "Männlich")}
          </MenuItem>
          <MenuItem value="female">
            {L("Female", "Weiblich")}
          </MenuItem>
          <MenuItem value="other">
            {L("Other", "Andere")}
          </MenuItem>
        </TextField>

        <TextField
          placeholder={L("Job / Profession", "Beruf")}
          value={form.job}
          onChange={handleChange("job")}
          fullWidth
          InputProps={inputStyle}
        />

        <TextField
            placeholder={L("Location / Region", "Ort / Region")}
            value={form.location}
            onChange={handleChange("location")}
            fullWidth
            InputProps={inputStyle}
          />
      </Box>

      {/* BUTTON */}
      <Box sx={{ mt: 4 }}>
        <Button
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            py: {
                xs: 1.6,
                sm: 1.8,
                md: 2.1,
            },
            fontSize: {
                xs: "0.9rem",
                sm: "1rem",
                md: "1.2rem",
            },
            background: "#c81e1e",
            color: "white",
            fontWeight: 700,
            borderRadius: 1.5,
            "&:hover": {
              background: "#a81818",
            },
          }}
        >
          {L("SAVE & CONTINUE", "SPEICHER & WEITER")}
        </Button>
      </Box>

      {/* ERROR */}
      {error && (
        <Typography
          sx={{
            mt: 2,
            color: "#ff4d4f",
            fontSize: "0.85rem",
            textAlign: "center",
          }}
        >
          {error}
        </Typography>
      )}

      {/* LOG OUT */}
      <Button
        fullWidth
        onClick={async () => {
          await supabase.auth.signOut();
          props.handlePageChange?.("Entry");
        }}
        sx={{
          mt: 2,
          py: {
            xs: 1.6,
            sm: 1.8,
            md: 2.1,
          },
          fontSize: {
            xs: "0.9rem",
            sm: "1rem",
            md: "1.2rem",
          },
          background: "#45434354",
          color: "white",
          fontWeight: 700,
          borderRadius: 1.5,
          "&:hover": {
            background: "#7568689f",
          },
        }}
      >
        {L("Log out", "Abmelden")}
      </Button>
    </Box>
  </Box>
);
}

/* INPUT STYLE (matches your login/register look) */
const inputStyle = {
  sx: {
    color: "white",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 1.5,

    "& input": {
      color: "white",
      padding: {
        xs: "14px 14px",
        sm: "16px 16px",
        md: "18px 18px",
      },
      fontSize: {
        xs: "0.9rem",
        sm: "1rem",
        md: "1.05rem",
      },
    },

    "& fieldset": {
      border: "1px solid rgba(255,255,255,0.1)",
    },

    "&:hover fieldset": {
      border: "1px solid rgba(255,255,255,0.2)",
    },

    "&.Mui-focused fieldset": {
      border: "1px solid #c81e1e",
    },
  },
};
